import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Upload, Trash2, RefreshCw, GripVertical, X } from 'lucide-react';

// Home page imports
import originalZeusSunset from '@/assets/original-zeus-sunset.jpg';
import heraFeature from '@/assets/hera-feature.jpg';
import originalZeusPool from '@/assets/original-zeus-pool.jpg';
import balconyViewSivota from '@/assets/balcony-view-sivota.jpg';
import originalHeraTerrace from '@/assets/original-hera-terrace.jpg';
import originalZeusCourtyard from '@/assets/original-zeus-courtyard.jpg';
import originalHeraView from '@/assets/original-hera-view.jpg';
import originalZeusLounge from '@/assets/original-zeus-lounge.jpg';
import originalHeraPanorama from '@/assets/original-hera-panorama.jpg';
import originalHeraLiving from '@/assets/original-hera-living.jpg';
import originalHeraBedroom from '@/assets/original-hera-bedroom.jpg';
import originalZeusInterior from '@/assets/original-zeus-interior.jpg';
import originalZeusDining from '@/assets/original-zeus-dining.jpg';
import paxos from '@/assets/paxos.jpg';
import antipaxos from '@/assets/antipaxos.jpg';
import corfu from '@/assets/corfu.jpg';
import sivotaAerial from '@/assets/sivota-aerial.jpg';

// Hera page imports
import heraHero from '@/assets/hera-hero.jpg';
import hera1 from '@/assets/hera-1.jpg';
import hera2 from '@/assets/hera-2.jpg';
import hera3 from '@/assets/hera-3.jpg';
import hera4 from '@/assets/hera-4.jpg';
import hera5 from '@/assets/hera-5.jpg';
import hera6 from '@/assets/hera-6.jpg';
import heraBed1 from '@/assets/hera-bed1.jpg';
import heraBed2 from '@/assets/hera-bed2.jpg';
import heraBed3 from '@/assets/hera-bed3.jpg';
import heraBed4 from '@/assets/hera-bed4.jpg';
import heraBed5 from '@/assets/hera-bed5.jpg';
import heraBed6 from '@/assets/hera-bed6.jpg';

// Zeus page imports
import zeus1 from '@/assets/zeus-1.jpg';
import zeus2 from '@/assets/zeus-2.jpg';
import zeus3 from '@/assets/zeus-3.jpg';
import zeus4 from '@/assets/zeus-4.jpg';
import zeus5 from '@/assets/zeus-5.jpg';
import zeus6 from '@/assets/zeus-6.jpg';
import zeus7 from '@/assets/zeus-7.jpg';
import zeus8 from '@/assets/zeus-8.jpg';
import zeus9 from '@/assets/zeus-9.jpg';
import zeus10 from '@/assets/zeus-10.jpg';
import zeusHero from '@/assets/zeus-hero.jpg';

// Gallery imports
import gallery1 from '@/assets/gallery-1.jpg';
import gallery2 from '@/assets/gallery-2.jpg';
import gallery3 from '@/assets/gallery-3.jpg';
import gallery4 from '@/assets/gallery-4.jpg';
import gallery5 from '@/assets/gallery-5.jpg';
import gallery6 from '@/assets/gallery-6.jpg';
import gallery7 from '@/assets/gallery-7.jpg';
import gallery8 from '@/assets/gallery-8.jpg';

// About imports
import hostDimitris from '@/assets/host-dimitris.jpg';
import originalZeusView from '@/assets/original-zeus-view.jpg';
import kitchenInterior from '@/assets/kitchen-interior.jpg';
import originalZeusBedroom from '@/assets/original-zeus-bedroom.jpg';
import originalZeusKitchen from '@/assets/original-zeus-kitchen.jpg';
import originalZeusBathroom from '@/assets/original-zeus-bathroom.jpg';

// Destinations imports
import beach from '@/assets/beach.jpg';
import boatTrip from '@/assets/boat-trip.jpg';
import harbour from '@/assets/harbour.jpg';
import terraceDining from '@/assets/terrace-dining.jpg';

// Booking imports
import hera2book from '@/assets/hera-2.jpg';
import zeus1book from '@/assets/zeus-1.jpg';
import zeus2book from '@/assets/zeus-2.jpg';

type Photo = { id: string; page: string; url: string; alt_text: string | null; display_order: number };
type AssetSection = { section: string; photos: { src: string; alt: string }[] };

const PAGES = ['home', 'hera', 'zeus', 'gallery', 'about', 'destinations', 'booking', 'contact'];

// Exact sections matching website structure
const ASSET_PHOTOS: Record<string, AssetSection[]> = {
  home: [
    { section: 'Hero', photos: [
      { src: originalZeusSunset, alt: 'Hero — Sunset over Ionian Sea' },
    ]},
    { section: 'Hera Apartment Card', photos: [
      { src: heraFeature, alt: 'Hera — Balcony dining with views' },
    ]},
    { section: 'Zeus Apartment Card', photos: [
      { src: originalZeusPool, alt: 'Zeus — Private pool and courtyard' },
    ]},
    { section: 'Cinematic Pull Quote', photos: [
      { src: balconyViewSivota, alt: 'Balcony breakfast panoramic' },
    ]},
    { section: 'Gallery — Sun, Sea & Stone', photos: [
      { src: balconyViewSivota, alt: 'Balcony breakfast view' },
      { src: originalZeusPool, alt: 'Private pool' },
      { src: originalHeraTerrace, alt: 'Sun-drenched terrace' },
      { src: originalZeusSunset, alt: 'Golden sunset' },
      { src: originalZeusCourtyard, alt: 'Stone courtyard' },
      { src: heraFeature, alt: 'Dining with views' },
      { src: originalHeraView, alt: 'Panoramic sea view' },
      { src: originalZeusLounge, alt: 'Lounge area' },
      { src: originalHeraPanorama, alt: 'Panoramic terrace' },
    ]},
    { section: 'Destinations — Island Hopping', photos: [
      { src: paxos, alt: 'Paxos' },
      { src: antipaxos, alt: 'Antipaxos' },
      { src: corfu, alt: 'Corfu' },
    ]},
    { section: 'Final CTA', photos: [
      { src: originalZeusSunset, alt: 'CTA background — sunset' },
    ]},
  ],
  hera: [
    { section: 'Hero', photos: [
      { src: heraHero, alt: 'Hera hero image' },
    ]},
    { section: 'Overview', photos: [
      { src: hera4, alt: 'Hera overview — bedroom detail' },
    ]},
    { section: 'Master Bedroom', photos: [
      { src: balconyViewSivota, alt: 'Balcony breakfast panoramic' },
      { src: hera1, alt: 'Master bedroom' },
      { src: hera2, alt: 'Bedroom view' },
      { src: hera3, alt: 'Bedroom sunset' },
    ]},
    { section: 'Secondary Bedroom', photos: [
      { src: hera4, alt: 'Secondary bedroom' },
      { src: hera5, alt: 'Bedroom detail' },
      { src: hera6, alt: 'Bedroom view' },
    ]},
    { section: 'Kitchen & Dining', photos: [
      { src: heraBed2, alt: 'Kitchen overview' },
      { src: heraBed3, alt: 'Kitchen detail' },
      { src: heraBed4, alt: 'Stone bar' },
      { src: heraBed5, alt: 'Dining area' },
      { src: heraBed6, alt: 'Kitchen window view' },
    ]},
    { section: 'Living Room', photos: [
      { src: heraBed1, alt: 'Living room' },
      { src: hera6, alt: 'Living room detail' },
    ]},
    { section: 'Views & Terrace (Panoramic)', photos: [
      { src: heraHero, alt: 'Hera panoramic view' },
    ]},
    { section: 'Booking CTA', photos: [
      { src: heraHero, alt: 'CTA background — Hera' },
    ]},
  ],
  zeus: [
    { section: 'Hero', photos: [
      { src: zeus1, alt: 'Zeus hero — courtyard pool' },
    ]},
    { section: 'Overview', photos: [
      { src: zeus1, alt: 'Zeus overview — courtyard pool' },
    ]},
    { section: 'Courtyard & Pool', photos: [
      { src: zeus1, alt: 'Courtyard pool panoramic' },
      { src: zeus2, alt: 'Pool detail' },
      { src: zeus3, alt: 'Bougainvillea' },
      { src: zeus4, alt: 'Stone veranda' },
      { src: zeus5, alt: 'Sun loungers' },
    ]},
    { section: 'Master Bedroom', photos: [
      { src: zeus6, alt: 'Master bedroom' },
      { src: zeus7, alt: 'Wooden furnishings' },
      { src: zeus8, alt: 'Bedroom veranda' },
    ]},
    { section: 'Twin Bedroom', photos: [
      { src: zeus9, alt: 'Twin bedroom' },
      { src: zeus10, alt: 'Balcony view' },
    ]},
    { section: 'Stone Veranda', photos: [
      { src: zeusHero, alt: 'Zeus panoramic veranda' },
    ]},
    { section: 'Booking CTA', photos: [
      { src: zeus1, alt: 'CTA background — Zeus' },
    ]},
  ],
  gallery: [
    { section: 'Gallery Grid', photos: [
      { src: gallery1, alt: 'Gallery 1' },
      { src: gallery2, alt: 'Gallery 2' },
      { src: gallery3, alt: 'Gallery 3' },
      { src: gallery4, alt: 'Gallery 4' },
      { src: gallery5, alt: 'Gallery 5' },
      { src: gallery6, alt: 'Gallery 6' },
      { src: gallery7, alt: 'Gallery 7' },
      { src: gallery8, alt: 'Gallery 8' },
    ]},
  ],
  about: [
    { section: 'Hero', photos: [
      { src: originalHeraPanorama, alt: 'About hero — panoramic terrace' },
    ]},
    { section: 'Opening — Welcome', photos: [
      { src: originalHeraTerrace, alt: 'Hera terrace view' },
    ]},
    { section: 'Editorial Gallery', photos: [
      { src: balconyViewSivota, alt: 'Balcony view' },
      { src: originalZeusPool, alt: 'Zeus pool' },
      { src: originalZeusCourtyard, alt: 'Stone courtyard' },
      { src: heraFeature, alt: 'Hera dining terrace' },
    ]},
    { section: 'The Setting', photos: [
      { src: originalHeraLiving, alt: 'Hera living room' },
    ]},
    { section: 'The Location', photos: [
      { src: originalZeusSunset, alt: 'Sunset view from Zeus' },
    ]},
    { section: 'Cinematic Quote', photos: [
      { src: kitchenInterior, alt: 'Traditional stone kitchen interior' },
    ]},
    { section: 'The Property — Every Detail', photos: [
      { src: originalZeusLounge, alt: 'Zeus stone lounge' },
      { src: originalHeraBedroom, alt: 'Hera bedroom' },
      { src: originalZeusDining, alt: 'Zeus dining' },
      { src: originalHeraPanorama, alt: 'Hera panoramic terrace' },
      { src: originalZeusInterior, alt: 'Zeus interior' },
      { src: originalZeusCourtyard, alt: 'Courtyard' },
      { src: originalZeusSunset, alt: 'Golden hour' },
    ]},
    { section: 'Meet Your Host — Dimitris', photos: [
      { src: hostDimitris, alt: 'Dimitris Vlachos — Your Host' },
    ]},
    { section: 'Final CTA', photos: [
      { src: originalZeusPool, alt: 'CTA background' },
    ]},
  ],
  destinations: [
    { section: 'Hero', photos: [
      { src: sivotaAerial, alt: 'Sivota aerial view' },
    ]},
    { section: 'Secluded Beaches', photos: [
      { src: beach, alt: 'Beach' },
    ]},
    { section: 'Boat Trips', photos: [
      { src: boatTrip, alt: 'Boat trip' },
    ]},
    { section: 'Sivota Harbour', photos: [
      { src: harbour, alt: 'Sivota harbour' },
    ]},
    { section: 'Local Dining', photos: [
      { src: terraceDining, alt: 'Terrace dining' },
    ]},
    { section: 'Islands — Corfu', photos: [
      { src: corfu, alt: 'Corfu' },
    ]},
    { section: 'Islands — Paxos', photos: [
      { src: paxos, alt: 'Paxos' },
    ]},
    { section: 'Islands — Antipaxos', photos: [
      { src: antipaxos, alt: 'Antipaxos' },
    ]},
  ],
  booking: [
    { section: 'Hero', photos: [
      { src: balconyViewSivota, alt: 'Booking hero — balcony view' },
    ]},
    { section: 'Property Image Strip', photos: [
      { src: heraFeature, alt: 'Hera apartment view' },
      { src: originalZeusPool, alt: 'Zeus pool area' },
      { src: hera1, alt: 'Hera interior' },
      { src: zeus1, alt: 'Zeus interior' },
      { src: hera2book, alt: 'Hera terrace' },
      { src: zeus2book, alt: 'Zeus lounge' },
    ]},
    { section: 'Apartment Selection — Hera Card', photos: [
      { src: heraFeature, alt: 'Hera card image' },
    ]},
    { section: 'Apartment Selection — Zeus Card', photos: [
      { src: originalZeusPool, alt: 'Zeus card image' },
    ]},
  ],
  contact: [
    { section: 'Hero', photos: [
      { src: harbour, alt: 'Contact hero — Sivota harbour' },
    ]},
  ],
};

type SelectedAsset = { src: string; alt: string; section: string; index: number };

export default function AdminPhotos() {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [selectedPage, setSelectedPage] = useState('home');
  const [uploading, setUploading] = useState(false);
  const [selectedUploadedPhoto, setSelectedUploadedPhoto] = useState<Photo | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<SelectedAsset | null>(null);
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);

  const load = async () => {
    const { data } = await supabase.from('photos').select('*').eq('page', selectedPage).order('display_order');
    setPhotos((data as Photo[]) ?? []);
  };

  useEffect(() => { load(); }, [selectedPage]);

  const upload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const path = `${selectedPage}/${Date.now()}-${file.name}`;
      const { error: uploadErr } = await supabase.storage.from('photos').upload(path, file);
      if (uploadErr) throw uploadErr;
      const { data: { publicUrl } } = supabase.storage.from('photos').getPublicUrl(path);
      await supabase.from('photos').insert({
        page: selectedPage,
        url: publicUrl,
        alt_text: file.name.replace(/\.[^/.]+$/, ''),
        display_order: photos.length,
      });
      toast.success('Photo uploaded');
      load();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const replaceUploadedPhoto = async (photo: Photo, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const urlParts = photo.url.split('/photos/');
      if (urlParts[1]) await supabase.storage.from('photos').remove([urlParts[1]]);
      const path = `${selectedPage}/${Date.now()}-${file.name}`;
      const { error: uploadErr } = await supabase.storage.from('photos').upload(path, file);
      if (uploadErr) throw uploadErr;
      const { data: { publicUrl } } = supabase.storage.from('photos').getPublicUrl(path);
      await supabase.from('photos').update({ url: publicUrl, alt_text: file.name.replace(/\.[^/.]+$/, '') }).eq('id', photo.id);
      toast.success('Photo replaced');
      setSelectedUploadedPhoto(null);
      load();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const removeUploadedPhoto = async (photo: Photo) => {
    const urlParts = photo.url.split('/photos/');
    if (urlParts[1]) await supabase.storage.from('photos').remove([urlParts[1]]);
    await supabase.from('photos').delete().eq('id', photo.id);
    toast.success('Photo removed');
    setSelectedUploadedPhoto(null);
    load();
  };

  const handleDragStart = (index: number) => { dragItem.current = index; };
  const handleDragEnter = (index: number) => { dragOverItem.current = index; };
  const handleDragEnd = async () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    const reordered = [...photos];
    const [removed] = reordered.splice(dragItem.current, 1);
    reordered.splice(dragOverItem.current, 0, removed);
    setPhotos(reordered);
    dragItem.current = null;
    dragOverItem.current = null;
    await Promise.all(reordered.map((p, i) =>
      supabase.from('photos').update({ display_order: i }).eq('id', p.id)
    ));
    toast.success('Order updated');
  };

  const sections = ASSET_PHOTOS[selectedPage] || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-3xl font-light">Photos</h1>
        <label className={`flex items-center gap-2 bg-accent text-accent-foreground px-4 py-2 text-sm tracking-wider uppercase rounded-sm cursor-pointer hover:bg-accent/90 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
          <Upload size={16} /> {uploading ? 'Uploading...' : 'Upload Photo'}
          <input type="file" accept="image/*" onChange={upload} className="hidden" />
        </label>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {PAGES.map(p => (
          <button key={p} onClick={() => { setSelectedPage(p); setSelectedUploadedPhoto(null); setSelectedAsset(null); }}
            className={`px-3 py-1.5 text-xs tracking-wider uppercase rounded-sm transition-colors ${selectedPage === p ? 'bg-accent text-accent-foreground' : 'bg-card text-muted-foreground border border-border hover:bg-muted'}`}>
            {p}
          </button>
        ))}
      </div>

      {/* Uploaded photos (draggable) */}
      {photos.length > 0 && (
        <div className="mb-8">
          <h3 className="text-sm text-muted-foreground uppercase tracking-wider mb-3">Uploaded Photos — drag to reorder</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map((photo, i) => (
              <div
                key={photo.id}
                draggable
                onDragStart={() => handleDragStart(i)}
                onDragEnter={() => handleDragEnter(i)}
                onDragEnd={handleDragEnd}
                onDragOver={e => e.preventDefault()}
                className={`relative group rounded-sm overflow-hidden border bg-card aspect-[4/3] cursor-grab active:cursor-grabbing transition-all ${
                  selectedUploadedPhoto?.id === photo.id ? 'border-accent ring-2 ring-accent/30' : 'border-border'
                }`}
                onClick={() => { setSelectedUploadedPhoto(selectedUploadedPhoto?.id === photo.id ? null : photo); setSelectedAsset(null); }}
              >
                <div className="absolute top-2 left-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                  <GripVertical size={16} className="text-white drop-shadow-md" />
                </div>
                <img src={photo.url} alt={photo.alt_text || ''} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
                <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent">
                  <p className="text-white text-xs truncate">{photo.alt_text}</p>
                </div>
              </div>
            ))}
          </div>
          {selectedUploadedPhoto && (
            <div className="mt-4 flex items-center gap-3 p-3 bg-card border border-border rounded-sm">
              <p className="text-sm flex-1">Selected: <span className="font-medium">{selectedUploadedPhoto.alt_text}</span></p>
              <label className="flex items-center gap-1.5 bg-accent text-accent-foreground px-3 py-1.5 text-xs tracking-wider uppercase rounded-sm cursor-pointer hover:bg-accent/90">
                <RefreshCw size={14} /> Replace
                <input type="file" accept="image/*" onChange={e => replaceUploadedPhoto(selectedUploadedPhoto, e)} className="hidden" />
              </label>
              <button onClick={() => removeUploadedPhoto(selectedUploadedPhoto)} className="flex items-center gap-1.5 bg-destructive text-destructive-foreground px-3 py-1.5 text-xs tracking-wider uppercase rounded-sm hover:bg-destructive/90">
                <Trash2 size={14} /> Delete
              </button>
              <button onClick={() => setSelectedUploadedPhoto(null)} className="p-1.5 text-muted-foreground hover:text-foreground"><X size={14} /></button>
            </div>
          )}
        </div>
      )}

      {/* Website sections with photos — click any to get actions */}
      {sections.map(section => (
        <div key={section.section} className="mb-8">
          <h3 className="text-sm text-muted-foreground uppercase tracking-wider mb-3">{section.section}</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {section.photos.map((asset, i) => {
              const isSelected = selectedAsset?.section === section.section && selectedAsset?.index === i;
              return (
                <div
                  key={`${section.section}-${i}`}
                  className={`relative rounded-sm overflow-hidden bg-card aspect-[4/3] cursor-pointer transition-all ${
                    isSelected ? 'border-2 border-accent ring-2 ring-accent/30' : 'border border-border/50 hover:border-accent/50'
                  }`}
                  onClick={() => {
                    setSelectedAsset(isSelected ? null : { src: asset.src, alt: asset.alt, section: section.section, index: i });
                    setSelectedUploadedPhoto(null);
                  }}
                >
                  <img src={asset.src} alt={asset.alt} className="w-full h-full object-cover" />
                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent">
                    <p className="text-white text-xs truncate">{asset.alt}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Action bar for selected asset photo */}
          {selectedAsset?.section === section.section && (
            <div className="mt-3 flex items-center gap-3 p-3 bg-card border border-border rounded-sm">
              <div className="flex-1">
                <p className="text-sm font-medium">{selectedAsset.alt}</p>
                <p className="text-xs text-muted-foreground">This is a bundled site photo. Upload a replacement to update it.</p>
              </div>
              <label className="flex items-center gap-1.5 bg-accent text-accent-foreground px-3 py-1.5 text-xs tracking-wider uppercase rounded-sm cursor-pointer hover:bg-accent/90">
                <RefreshCw size={14} /> Replace
                <input type="file" accept="image/*" onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setUploading(true);
                  try {
                    const path = `${selectedPage}/${Date.now()}-${file.name}`;
                    const { error: uploadErr } = await supabase.storage.from('photos').upload(path, file);
                    if (uploadErr) throw uploadErr;
                    const { data: { publicUrl } } = supabase.storage.from('photos').getPublicUrl(path);
                    await supabase.from('photos').insert({
                      page: selectedPage,
                      url: publicUrl,
                      alt_text: `${selectedAsset.section} — ${file.name.replace(/\.[^/.]+$/, '')}`,
                      display_order: photos.length,
                    });
                    toast.success('Replacement photo uploaded');
                    setSelectedAsset(null);
                    load();
                  } catch (err: any) {
                    toast.error(err.message);
                  } finally {
                    setUploading(false);
                  }
                }} className="hidden" />
              </label>
              <button onClick={() => setSelectedAsset(null)} className="p-1.5 text-muted-foreground hover:text-foreground"><X size={14} /></button>
            </div>
          )}
        </div>
      ))}

      {photos.length === 0 && sections.length === 0 && (
        <div className="py-12 text-center text-muted-foreground">No photos for this page yet. Upload some!</div>
      )}
    </div>
  );
}
